import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Chip, emphasize, IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CancelIcon from '@mui/icons-material/Cancel';
import { ClassNameMap } from '@mui/styles';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelValue, TimeSignalClassification } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessages } from 'modules/intl';
import { isKeyPressed } from 'modules/common/hooks/isKeyPressed';
import { useChipState } from '../../hooks/useChipState';
import { useIsSuggestion } from '../../hooks/useIsSuggestion';
import { useLabelState } from '../../../timesheet-labels/hooks/useLabelState';
import { LabelState } from '../../../timesheet-labels/hooks';
import { NOTE_FIELDS } from '../../../notes/common';
import { useTimesheetLabelSuggestionContext } from '../../../timesheet-labels';

type SimpleChipProps = {
  disablePortalOnTooltip?: boolean;
  className?: string;
  label?: TimesheetLabel | null;
  onChipClick?: (event: any) => void;
  handleDelete?: (label: TimesheetLabel) => void;
  disabled?: boolean;
  size?: 'medium' | 'small';
  deleteEnabled?: boolean;
  chipRef?: React.MutableRefObject<HTMLDivElement | null>;
  classes?: ClassNameMap;
  acceptSuggestion?: (value: LabelValue | null) => void;
};

interface UseChipStylesProps {
  classes: ClassNameMap<string> | undefined;
  isEmptyLabelsEnabled: boolean;
  labelState: LabelState;
}

const useChipStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '100%',
    minWidth: '70px'
  },
  clickableColorPrimary: ({
    isEmptyLabelsEnabled,
    labelState
  }: UseChipStylesProps) => ({
    ...(isEmptyLabelsEnabled
      ? {}
      : {
          color: theme.palette.timesheetLabel.chipClickableColor,
          backgroundColor: theme.palette.timesheetLabel.nonEmptylabel
        }),
    '&:hover, &:focus': {
      background:
        labelState === LabelState.SUGGESTED
          ? undefined
          : emphasize(
              !isEmptyLabelsEnabled
                ? theme.palette.timesheetLabel.nonEmptylabel
                : labelState === LabelState.OMITTED
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              0.08
            )
    },
    '&:active': {
      background:
        labelState === LabelState.SUGGESTED
          ? undefined
          : emphasize(
              !isEmptyLabelsEnabled
                ? theme.palette.timesheetLabel.nonEmptylabel
                : labelState === LabelState.OMITTED
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              0.12
            )
    }
  }),
  // TIDP-1774: Remove suggestionOutlinedSecondary and outlinedSecondary classes when isEmptyLabelsEnabled FF is fully rolled out.
  suggestionOutlinedSecondary: {
    color: theme.palette.timesheetLabel.nonEmptylabel,
    borderColor: theme.palette.timesheetLabel.nonEmptylabel,
    borderStyle: 'dashed'
  },
  outlinedSecondary: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    borderStyle: 'solid'
  },
  suggestionOutlinedPrimary: {
    borderStyle: 'dashed'
  },
  omissionOutlinedPrimary: {
    backgroundColor: theme.palette.primary.light
  },
  suggestionAccept: {
    color: theme.palette.success.dark
  },
  suggestionReject: {
    color: theme.palette.error.dark
  }
}));

const message = [
  'timesheetLabels.acceptSuggestion',
  'timesheetLabels.denySuggestion',
  'timesheetLabels.removeBtn'
];

export const SimpleChip: React.FC<SimpleChipProps> = ({
  className,
  label,
  onChipClick,
  handleDelete,
  disabled = false,
  size = 'medium',
  deleteEnabled = true,
  chipRef,
  disablePortalOnTooltip,
  classes: overrideClasses,
  acceptSuggestion
}: SimpleChipProps) => {
  const { control } = useFormContext();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const { disableSuggestions } = useTimesheetLabelSuggestionContext();
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const isSuggestion = useIsSuggestion(label);
  const labelState = useLabelState(label, disableSuggestions);
  const noteClassification = useWatch({
    name: NOTE_FIELDS.CLASSIFICATION,
    control
  });
  const classes = useChipStyles({
    classes: overrideClasses,
    isEmptyLabelsEnabled: isEmptyLabelsEnabled,
    labelState: labelState
  });
  const {
    isEmptyValue: isEmpty,
    chipLabel,
    toolTipText,
    isDisabled
  } = useChipState(label, disabled);
  const chipClasses = useMemo(() => {
    const cls: ClassNameMap = {
      root: classes.root,
      clickableColorPrimary: classes.clickableColorPrimary
    };
    //TIDP-1774: Remove outlinedSecondary when isEmptyLabelsEnabled is fully rolled out.
    cls['outlinedSecondary'] = isEmptyLabelsEnabled
      ? ''
      : isSuggestion && !isEmpty
      ? classes.suggestionOutlinedSecondary
      : classes.outlinedSecondary;
    cls['outlinedPrimary'] =
      isEmptyLabelsEnabled && labelState === LabelState.SUGGESTED
        ? classes.suggestionOutlinedPrimary
        : labelState === LabelState.OMITTED
        ? classes.omissionOutlinedPrimary
        : '';

    return cls;
  }, [classes, isSuggestion, isEmpty, isEmptyLabelsEnabled, labelState]);
  const clickableChiptestID = useMemo(
    () => `chip${onChipClick ? '_click' : ''}_${label?.name}_${label?.id}`,
    [onChipClick, label]
  );

  const chipParentRef = useRef<HTMLDivElement>(null);
  const [focusElement, setFocusElement] = useState({
    isFocus: false,
    width: 0
  });

  const mouseEnter = useCallback(() => {
    setFocusElement({
      isFocus: true,
      width: chipParentRef.current?.clientWidth ?? 0
    });
  }, [setFocusElement]);

  const mouseLeave = useCallback(() => {
    setFocusElement({ isFocus: false, width: 0 });
  }, [setFocusElement]);

  const onFocusChipWidth = useMemo(() => {
    return focusElement.isFocus && !isEmpty
      ? { style: { width: focusElement.width } }
      : null;
  }, [focusElement, isEmpty]);

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const onDelete = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (label && handleDelete) handleDelete(label);
      if (isTiwScreenReaderEnabled) {
        chipRef?.current?.focus();
      }
    },
    [chipRef, handleDelete, isTiwScreenReaderEnabled, label]
  );

  const onChipDelete = useMemo(() => {
    return focusElement.isFocus &&
      (isEmptyLabelsEnabled &&
      noteClassification !== TimeSignalClassification.Accepted
        ? labelState !== LabelState.NOT_SELECTED
        : !isEmpty) &&
      deleteEnabled
      ? {
          onDelete: onDelete
        }
      : null;
  }, [
    noteClassification,
    focusElement.isFocus,
    isEmptyLabelsEnabled,
    labelState,
    isEmpty,
    deleteEnabled,
    onDelete
  ]);

  const onAcceptSuggestion = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? label?.suggestedValues?.[0]
        : label?.value;
      acceptSuggestion?.(value ?? null);
      if (isTiwScreenReaderEnabled) {
        chipRef?.current?.focus();
      }
    },
    [
      acceptSuggestion,
      chipRef,
      isTimeSignalFederatedSuggestionsEnabled,
      isTiwScreenReaderEnabled,
      label?.suggestedValues,
      label?.value
    ]
  );

  useEffect(() => {
    if (chipParentRef) {
      mouseLeave();
    }
  }, [chipParentRef, label, mouseLeave]);

  const messageLabel = useMessages(message);

  const deleteIcon = useMemo(
    () =>
      (
        isEmptyLabelsEnabled
          ? labelState === LabelState.SUGGESTED
          : isSuggestion && !isEmpty
      ) ? (
        <>
          <IconButton
            size="small"
            data-testid={`accept-icon-button-${clickableChiptestID}`}
            onClick={onAcceptSuggestion}
            aria-label={
              isTiwScreenReaderEnabled
                ? messageLabel['timesheetLabels.acceptSuggestion']
                : ''
            }
          >
            <CheckCircleOutlineOutlinedIcon
              fontSize="small"
              className={classes.suggestionAccept}
            />
          </IconButton>
          <IconButton
            size="small"
            data-testid={`reject-icon-button-${clickableChiptestID}`}
            onClick={onDelete}
            aria-label={
              isTiwScreenReaderEnabled
                ? messageLabel['timesheetLabels.denySuggestion']
                : ''
            }
          >
            <RemoveCircleOutlineOutlinedIcon
              fontSize="small"
              className={classes.suggestionReject}
            />
          </IconButton>
        </>
      ) : (
        <IconButton
          size="small"
          data-testid={`clear-icon-button-${clickableChiptestID}`}
          aria-label={
            isTiwScreenReaderEnabled
              ? messageLabel['timesheetLabels.removeBtn']
              : ''
          }
        >
          <CancelIcon fontSize={'small'} />
        </IconButton>
      ),
    [
      isEmptyLabelsEnabled,
      labelState,
      isSuggestion,
      isEmpty,
      clickableChiptestID,
      onAcceptSuggestion,
      isTiwScreenReaderEnabled,
      messageLabel,
      classes.suggestionAccept,
      classes.suggestionReject,
      onDelete
    ]
  );

  const hoverForDeleteProps = useMemo(
    () =>
      deleteEnabled
        ? {
            onMouseEnter: mouseEnter,
            onMouseLeave: mouseLeave,
            onFocus: mouseEnter,
            ...onFocusChipWidth
          }
        : undefined,
    [deleteEnabled, mouseEnter, mouseLeave, onFocusChipWidth]
  );

  const { formatMessage } = useIntl();
  const timesheetButtonMsg = formatMessage(
    {
      id: 'timesheetLabels.editTimesheetLable'
    },
    { value: chipLabel }
  );

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const isEscKeyPressed = isKeyPressed(event.key, 'Escape');

      if (isTiwScreenReaderEnabled && isEscKeyPressed) {
        chipRef?.current?.focus();
      }
    },
    [chipRef, isTiwScreenReaderEnabled]
  );

  const getChipVariant = useCallback(() => {
    if (isEmptyLabelsEnabled) {
      if (labelState === LabelState.SELECTED) {
        return 'filled';
      }

      return 'outlined';
    }
    return !isEmpty && !isSuggestion ? 'filled' : 'outlined';
  }, [labelState, isEmpty, isSuggestion, isEmptyLabelsEnabled]);

  const getChipColor = useCallback(() => {
    if (isEmptyLabelsEnabled) {
      if (
        labelState === LabelState.SELECTED ||
        labelState === LabelState.OMITTED ||
        labelState === LabelState.SUGGESTED
      ) {
        return 'primary';
      }

      return 'error';
    }
    return !isEmpty && !isSuggestion ? 'primary' : 'secondary';
  }, [labelState, isEmpty, isSuggestion, isEmptyLabelsEnabled]);

  return (
    <div
      ref={chipParentRef}
      {...hoverForDeleteProps}
      data-testid={`${clickableChiptestID}-container`}
    >
      <Tooltip
        title={toolTipText}
        PopperProps={{ disablePortal: disablePortalOnTooltip }}
        disableInteractive
      >
        <Chip
          onKeyUp={handleKeyUp}
          className={className}
          ref={chipRef}
          disabled={isDisabled}
          data-testid={clickableChiptestID}
          label={chipLabel}
          variant={getChipVariant()}
          color={getChipColor()}
          size={size}
          clickable={!disabled}
          classes={chipClasses}
          onClick={onChipClick}
          {...onChipDelete}
          deleteIcon={deleteIcon}
          aria-label={isTiwScreenReaderEnabled ? timesheetButtonMsg : ''}
        />
      </Tooltip>
    </div>
  );
};
